import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { is } from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { debounce } from 'lodash';
import { AltTextBadge } from 'mastodon/components/alt_text_badge';
import { Blurhash } from 'mastodon/components/blurhash';
import { formatTime } from 'mastodon/features/video';
import { openModal } from 'mastodon/actions/modal';
import { autoPlayGif, displayMedia, useBlurhash } from '../initial_state';
import { reportSensitive } from 'mastodon/actions/interactions';
import { EyeSlashIcon } from './icons/eye_slash';

// Maximum number of items rendered in the grid (Facebook-style: 2×2).
// Items beyond this are hidden behind a "+N" overlay on the last cell.
const GRID_DISPLAY_LIMIT = 4;

class Item extends PureComponent {
  static propTypes = {
    attachment: ImmutablePropTypes.map.isRequired,
    lang: PropTypes.string,
    standalone: PropTypes.bool,
    index: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired,
    onClick: PropTypes.func.isRequired,
    displayWidth: PropTypes.number,
    visible: PropTypes.bool.isRequired,
    autoplay: PropTypes.bool,
    // When > 0 this item is the last visible cell and shows a "+N more" overlay
    remainingCount: PropTypes.number,
  };

  static defaultProps = {
    standalone: false,
    index: 0,
    size: 1,
  };

  state = {
    loaded: false,
  };

  handleMouseEnter = (e) => {
    if (this.hoverToPlay()) {
      e.target.play();
    }
  };

  handleMouseLeave = (e) => {
    if (this.hoverToPlay()) {
      e.target.pause();
      e.target.currentTime = 0;
    }
  };

  getAutoPlay() {
    return false;
  }

  hoverToPlay() {
    const { attachment } = this.props;
    return !this.getAutoPlay() && ['gifv', 'video'].includes(attachment.get('type'));
  }

  handleClick = (e) => {
    const { index, onClick } = this.props;

    if (e.button === 0 && !(e.ctrlKey || e.metaKey)) {
      if (this.hoverToPlay()) {
        e.target.pause();
        e.target.currentTime = 0;
      }
      e.preventDefault();
      onClick(index);
    }

    e.stopPropagation();
  };

  handleImageLoad = () => {
    this.setState({ loaded: true });
  };

  render() {
    const { attachment, lang, index, size, standalone, visible, remainingCount } = this.props;

    let badges = [], thumbnail;

    let width = 50;
    let height = 100;

    // For display purposes we treat any size > 4 as size 4 (2×2 grid)
    const displaySize = Math.min(size, GRID_DISPLAY_LIMIT);

    if (displaySize === 1) {
      width = 100;
    }

    if (displaySize === 4 || (displaySize === 3 && index > 0)) {
      height = 50;
    }

    const description = attachment.getIn(['translation', 'description']) || attachment.get('description');

    if (description?.length > 0) {
      badges.push(<AltTextBadge key='alt' description={description} />);
    }

    if (attachment.get('type') === 'unknown') {
      return (
        <div className={classNames('media-gallery__item', { standalone, 'media-gallery__item--tall': height === 100, 'media-gallery__item--wide': width === 100 })} key={attachment.get('id')}>
          <a className='media-gallery__item-thumbnail' style={{ cursor: 'pointer' }} title={description} lang={lang} target='_blank' rel='noopener'>
            <Blurhash
              hash={attachment.get('blurhash')}
              className='media-gallery__preview'
              dummy={!useBlurhash}
            />
          </a>
        </div>
      );
    } else if (attachment.get('type') === 'image') {
      const previewUrl = attachment.get('preview_url');

      const focusX = attachment.getIn(['meta', 'focus', 'x']) || 0;
      const focusY = attachment.getIn(['meta', 'focus', 'y']) || 0;
      const x = ((focusX / 2) + .5) * 100;
      const y = ((focusY / -2) + .5) * 100;

      thumbnail = (
        <div
          className='media-gallery__item-thumbnail'
          onClick={this.handleClick}
        >
          <img
            src={previewUrl}
            alt={description}
            lang={lang}
            loading='lazy'
            decoding='async'
            style={{ objectPosition: `${x}% ${y}%` }}
            onLoad={this.handleImageLoad}
          />
        </div>
      );
    } else if (['gifv', 'video'].includes(attachment.get('type'))) {
      const autoPlay = this.getAutoPlay();
      const duration = attachment.getIn(['meta', 'original', 'duration']);

      if (attachment.get('type') === 'gifv') {
        badges.push(<span key='gif' className='media-gallery__alt__label media-gallery__alt__label--non-interactive'>GIF</span>);
      } else {
        badges.push(<span key='video' className='media-gallery__alt__label media-gallery__alt__label--non-interactive'>{formatTime(Math.floor(duration))}</span>);
      }

      thumbnail = (
        <div className={classNames('media-gallery__gifv', { autoplay: autoPlay })}>
          <video
            className='media-gallery__item-gifv-thumbnail'
            aria-label={description}
            lang={lang}
            role='application'
            src={attachment.get('url')}
            poster={attachment.get('preview_url')}
            preload='none'
            onClick={this.handleClick}
            onMouseEnter={this.handleMouseEnter}
            onMouseLeave={this.handleMouseLeave}
            onLoadedData={this.handleImageLoad}
            autoPlay={autoPlay}
            playsInline
            loop
            muted={true}
          />
        </div>
      );
    }

    return (
      <div className={classNames('media-gallery__item', { standalone, 'media-gallery__item--tall': height === 100, 'media-gallery__item--wide': width === 100 })} key={attachment.get('id')} >
        <Blurhash
          hash={attachment.get('blurhash')}
          dummy={!useBlurhash}
          className={classNames('media-gallery__preview', {
            'media-gallery__preview--hidden': visible && this.state.loaded,
          })}
        />

        {visible && thumbnail}

        {visible && badges && (
          <div className='media-gallery__item__badges'>
            {badges}
          </div>
        )}

        {/* Facebook-style "+N" overlay on the last visible cell */}
        {visible && remainingCount > 0 && (
          <div className='media-gallery__more-overlay' onClick={this.handleClick}>
            <span className='media-gallery__more-overlay__count'>+{remainingCount}</span>
          </div>
        )}
      </div>
    );
  }
}

class MediaGallery extends PureComponent {
  static propTypes = {
    sensitive: PropTypes.bool,
    media: ImmutablePropTypes.list.isRequired,
    lang: PropTypes.string,
    size: PropTypes.object,
    height: PropTypes.number.isRequired,
    onOpenMedia: PropTypes.func.isRequired,
    defaultWidth: PropTypes.number,
    cacheWidth: PropTypes.func,
    visible: PropTypes.bool,
    autoplay: PropTypes.bool,
    onToggleVisibility: PropTypes.func,
    statusId: PropTypes.string.isRequired,
    accountId: PropTypes.string.isRequired,
  };

  state = {
    visible: this.props.visible !== undefined ? this.props.visible : (displayMedia !== 'hide_all' && !this.props.sensitive || displayMedia === 'show_all'),
    width: this.props.defaultWidth,
    reported: false, // New state to track if the post has been reported
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize, { passive: true });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!is(nextProps.media, this.props.media) && nextProps.visible === undefined) {
      this.setState({ visible: displayMedia !== 'hide_all' && !nextProps.sensitive || displayMedia === 'show_all' });
    } else if (!is(nextProps.visible, this.props.visible) && nextProps.visible !== undefined) {
      this.setState({ visible: nextProps.visible });
    }
    if (!this.props.sensitive && nextProps.sensitive) {
      this.setState({ visible: false });
    }
  }

  handleResize = debounce(() => {
    if (this.node) {
      this._setDimensions();
    }
  }, 250, {
    trailing: true,
  });

  handleOpen = () => {
    if (this.props.onToggleVisibility) {
      this.props.onToggleVisibility();
    } else {
      this.setState({ visible: !this.state.visible });
    }
  };

  handleReport = () => {
    const { dispatch, statusId, accountId } = this.props;
    dispatch(reportSensitive(statusId, accountId, () => {
      this.setState({ visible: false, reported: true });
    }));
  };

  handleClick = (index) => {
    this.props.onOpenMedia(this.props.media, index, this.props.lang);
  };

  handleRef = c => {
    this.node = c;

    if (this.node) {
      this._setDimensions();
    }
  };

  _setDimensions() {
    const width = this.node.offsetWidth;

    if (this.props.cacheWidth) {
      this.props.cacheWidth(width);
    }

    this.setState({
      width: width,
    });
  }

  isFullSizeEligible() {
    const { media } = this.props;
    return media.size === 1 && media.getIn([0, 'meta', 'small', 'aspect']);
  }

  render() {
    const { media, lang, sensitive, defaultWidth, autoplay } = this.props;
    const { visible, reported } = this.state;
    const width = this.state.width || defaultWidth;

    let children, spoilerButton;

    const style = {};

    if (this.isFullSizeEligible()) {
      style.aspectRatio = `${this.props.media.getIn([0, 'meta', 'small', 'aspect'])}`;
    } else {
      style.aspectRatio = '3 / 2';
    }

    const size = media.size;
    const uncached = media.every(attachment => attachment.get('type') === 'unknown');

    // Facebook-style: cap the number of visible cells at GRID_DISPLAY_LIMIT.
    // Remaining items are accessible via the carousel after clicking.
    const displayCount  = size > GRID_DISPLAY_LIMIT ? GRID_DISPLAY_LIMIT : size;
    const remainingCount = size > GRID_DISPLAY_LIMIT ? size - GRID_DISPLAY_LIMIT : 0;
    const displayMedia  = media.take(displayCount);

    if (this.isFullSizeEligible()) {
      children = <Item standalone autoplay={autoplay} onClick={this.handleClick} attachment={media.get(0)} lang={lang} displayWidth={width} visible={visible} />;
    } else {
      children = displayMedia.map((attachment, i) => (
        <Item
          key={attachment.get('id')}
          autoplay={autoplay}
          onClick={this.handleClick}
          attachment={attachment}
          index={i}
          lang={lang}
          size={size}
          displayWidth={width}
          visible={visible || uncached}
          remainingCount={i === displayCount - 1 ? remainingCount : 0}
        />
      ));
    }

    if (uncached) {
      spoilerButton = (
        <button type='button' disabled className='spoiler-button__overlay'>
          <span className='spoiler-button__overlay__label'>
            <EyeSlashIcon className="spoiler-button__icon" style={{ width: '36px', height: '36px', marginRight: '8px'}} />
            <FormattedMessage id='status.uncached_media_warning' defaultMessage='Preview not available' />
            <span className='spoiler-button__overlay__action'><FormattedMessage id='status.media.open' defaultMessage='Click to open' /></span>
          </span>
        </button>
      );
    } else if (!visible) {
      spoilerButton = (
        <button type='button' onClick={!reported && this.handleOpen} className='spoiler-button__overlay'>
          <span className='spoiler-button__overlay__label'>
            <EyeSlashIcon className="spoiler-button__icon" style={{ width: '36px', height: '36px', marginRight: '8px'}} />
            {sensitive ? (
              <FormattedMessage id='status.sensitive_warning' defaultMessage='Sensitive content' />
            ) : (
              <FormattedMessage id='status.media_hidden' defaultMessage='Media hidden' />
            )}
            <span className='spoiler-button__overlay__action'>
              {reported? <FormattedMessage id='status.reported_hidden' defaultMessage='Post hidden due to reported violation' /> : <FormattedMessage id='status.media.show' defaultMessage='Click to show' />}
            </span>
          </span>
        </button>
      );
    }

    // Cap the layout class at GRID_DISPLAY_LIMIT so CSS grid rules (--layout-1..4)
    // always apply correctly regardless of actual attachment count.
    const layoutClass = Math.min(size, GRID_DISPLAY_LIMIT);

    return (
      <div className={`media-gallery media-gallery--layout-${layoutClass}`} style={{  minHeight: '50vh', maxHeight: '70svh' }} ref={this.handleRef}>
        {children}

        {(!visible || uncached) && (
          <div className={classNames('spoiler-button', { 'spoiler-button--click-thru': uncached })}>
            {spoilerButton}
          </div>
        )}

        {(this.props.sensitive && visible && !uncached && !reported) && (
          <div className='media-gallery__actions'>
            <button className='media-gallery__actions__pill' onClick={this.handleReport}><FormattedMessage id='media_gallery.report' defaultMessage='Report' /></button>
          </div>
        )}
      </div>
    );
  }
}

export default connect()(MediaGallery);
